import { apiFetch } from '@/lib/api/apiClient';
import { WS_BRIDGE_API_URL, WS_LOCALHOST_URL, WS_URL } from '@/constants/constants';
import { NetworkError } from '@/utils/errors';

export type WebSocketMessage = Record<string, unknown>;

export interface WebSocketOptions {
    onOpen?: (event: Event) => void;
    onMessage?: (data: WebSocketMessage) => void;
    onError?: (error: Error) => void;
    onClose?: (event: CloseEvent) => void;
    autoReconnect?: boolean;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
    silentReconnect?: boolean;
    preferLocalhost?: boolean;
    isBridgeApi?: boolean;
}

export class WebSocketClient {
    private ws: WebSocket | null = null;
    private readonly path: string;
    private readonly options: Required<Omit<WebSocketOptions, 'preferLocalhost'>> & { preferLocalhost: boolean };
    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private shouldReconnect = true;
    private messageQueue: WebSocketMessage[] = [];
    private hasConnectedOnce = false;
    private isUsingLocalhost = false;
    private triedLocalhostThisAttempt = false;
    private isTokenRefreshing = false;

    constructor(path: string, options: WebSocketOptions = {}) {
        this.path = path;
        this.options = {
            onOpen: options.onOpen ?? (() => {}),
            onMessage: options.onMessage ?? (() => {}),
            onError: options.onError ?? (() => {}),
            onClose: options.onClose ?? (() => {}),
            autoReconnect: options.autoReconnect ?? true,
            reconnectInterval: options.reconnectInterval ?? 5000,
            maxReconnectAttempts: options.maxReconnectAttempts ?? Infinity,
            silentReconnect: options.silentReconnect ?? true,
            preferLocalhost: options.preferLocalhost ?? false,
            isBridgeApi: options.isBridgeApi ?? false,
        };
    }

    /** WebSocket接続を開始 */
    connect(): void {
        if (this.isTokenRefreshing) return;
        // localhost優先モードの場合
        if (this.options.preferLocalhost && WS_LOCALHOST_URL) {
            if (!this.triedLocalhostThisAttempt) {
                this.connectToLocalhost();
            }
            // localhost優先モードではCloudにフォールバックしない
        } else {
            // 通常モード（既存の動作）
            this.connectToCloud();
        }
    }

    /** localhost接続 */
    private connectToLocalhost(): void {
        this.triedLocalhostThisAttempt = true;
        const cleanPath = this.path.startsWith('/') ? this.path : `/${this.path}`;
        const wsUrl = WS_LOCALHOST_URL + cleanPath;
        try {
            this.ws = new WebSocket(wsUrl);
            this.isUsingLocalhost = true;

            // 1秒タイムアウト
            const timeoutId = setTimeout(() => {
                if (!this.hasConnectedOnce && this.ws?.readyState !== WebSocket.OPEN) {
                    if (this.ws) {
                        this.ws.close();
                    }
                }
            }, 1000);

            this.ws.onopen = (event) => {
                clearTimeout(timeoutId);
                this.reconnectAttempts = 0;
                this.hasConnectedOnce = true;

                while (this.messageQueue.length > 0) {
                    const msg = this.messageQueue.shift();
                    if (msg) this.send(msg);
                }

                this.options.onOpen(event);
            };

            this.ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data) as WebSocketMessage;
                    this.options.onMessage(data);
                } catch (error) {
                    console.error('[WS][local] Parse error:', error);
                }
            };

            this.ws.onerror = () => {
                clearTimeout(timeoutId);
                if (!this.shouldReconnect) return;
                if (!this.hasConnectedOnce) {
                    this.options.onError(new NetworkError('Bridge not available'));
                } else {
                    this.options.onError(new NetworkError('WebSocket error'));
                }
            };

            this.ws.onclose = (event) => {
                clearTimeout(timeoutId);

                if (this.hasConnectedOnce) {
                    this.options.onClose(event);
                }
                if (this.shouldReconnect && this.options.autoReconnect) {
                    this.scheduleReconnect();
                }
            };
        } catch (error) {
            this.connectToCloud();
        }
    }

    /** Cloud接続 */
    private connectToCloud(): void {
        const baseUrl = this.options.isBridgeApi ? WS_BRIDGE_API_URL : WS_URL;
        const wsUrl = baseUrl + '/ws' + this.path;

        const ws = new WebSocket(wsUrl);
        this.ws = ws;
        this.isUsingLocalhost = false;

        ws.onopen = (event) => {
            if (ws !== this.ws) return; // 追加
            this.reconnectAttempts = 0;
            this.hasConnectedOnce = true;
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            } // 追加
            this.options.onOpen(event);
        };

        ws.onmessage = (event) => {
            if (ws !== this.ws) return;
            try {
                const data = JSON.parse(event.data) as WebSocketMessage;
                this.options.onMessage(data);
            } catch (error) {
                console.error('[WS][cloud] Parse error:', error, event.data);
            }
        };

        ws.onerror = () => {
            if (ws !== this.ws) return; // 追加
            if (!this.hasConnectedOnce || !this.options.silentReconnect) {
                this.options.onError(new NetworkError('WebSocket error'));
            }
        };

        ws.onclose = (event) => {
            if (ws !== this.ws) return; // 追加: 古いwsのclose無視
            if (this.isTokenRefreshing) return;

            if (event.code === 1008 || event.code === 1006) {
                this.handleTokenRefresh();
                return;
            }

            if (!this.hasConnectedOnce || !this.options.silentReconnect) {
                this.options.onClose(event);
            }

            if (this.shouldReconnect && this.options.autoReconnect) {
                this.scheduleReconnect();
            }
        };
    }

    /** トークンリフレッシュ処理 */
    private async handleTokenRefresh(): Promise<void> {
        if (this.isTokenRefreshing) return;
        this.isTokenRefreshing = true;
        try {
            await apiFetch('/auth/refresh', {
                method: 'POST',
                credentials: 'include',
            });
            this.isTokenRefreshing = false;
            this.reconnectAttempts = 0;
            this.connect();
        } catch (error) {
            this.isTokenRefreshing = false;
            this.shouldReconnect = false;
            this.options.onError(new Error('Authentication failed'));
            this.options.onClose(new CloseEvent('close', { code: 1008, reason: 'Auth failed' }));
        }
    }

    /** 再接続をスケジュール */
    private scheduleReconnect(): void {
        if (this.reconnectTimer) return; // 追加: 多重防止

        this.reconnectAttempts++;
        if (this.reconnectAttempts > this.options.maxReconnectAttempts) {
            this.shouldReconnect = false;
            this.options.onError(new NetworkError('Max reconnect attempts reached'));
            return;
        }

        const interval = this.options.reconnectInterval;

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.triedLocalhostThisAttempt = false;
            this.connect();
        }, interval);
    }

    /** メッセージを送信 */
    send(data: WebSocketMessage | string): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            const message = typeof data === 'string' ? data : JSON.stringify(data);
            this.ws.send(message);
        } else {
            console.warn('[WS] Not connected, queueing message');
            const msg = typeof data === 'string' ? JSON.parse(data) : data;
            this.messageQueue.push(msg);
        }
    }

    /** 接続を閉じる */
    close(code = 1000, reason = 'Client closed connection'): void {
        this.shouldReconnect = false;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.ws) {
            this.ws.close(code, reason);
            this.ws = null;
        }
    }

    /** 現在の接続状態 */
    get readyState(): number {
        return this.ws?.readyState ?? WebSocket.CLOSED;
    }

    /** 接続中かどうか */
    get isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    /** 再接続試行回数 */
    get reconnectCount(): number {
        return this.reconnectAttempts;
    }

    /** localhost接続中かどうか */
    get isConnectedToLocalhost(): boolean {
        return this.isUsingLocalhost && this.isConnected;
    }
}

/** 単発のリクエスト/レスポンス型WebSocket通信 */
export async function wsRequest<T = WebSocketMessage>(
    path: string,
    request: WebSocketMessage,
    timeout = 30000,
): Promise<T> {
    return new Promise((resolve, reject) => {
        let ws: WebSocketClient | null = null;

        const timeoutId = setTimeout(() => {
            if (ws) {
                ws.close(1000, 'Request timeout');
            }
            reject(new Error('WebSocket request timeout'));
        }, timeout);

        ws = new WebSocketClient(path, {
            onOpen: () => {
                if (ws) {
                    ws.send(request);
                }
            },
            onMessage: (data) => {
                clearTimeout(timeoutId);
                if (ws) {
                    ws.close(1000, 'Request completed');
                }
                resolve(data as T);
            },
            onError: (error) => {
                clearTimeout(timeoutId);
                if (ws) {
                    ws.close(1000, 'Request failed');
                }
                reject(error);
            },
            autoReconnect: false,
        });

        ws.connect();
    });
}
